import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { WeightDiagram } from '../../components/Diagrams';

export default function KoerekortOgTrailer() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" },
    { name: "Kørekort og trailer", path: "/koerekort-og-trailer" }
  ];
  const tocItems = [
    { id: "kategori-b", title: "Kategori B: Almindeligt kørekort" },
    { id: "750-kg-reglen", title: "750 kg-reglen og 3.500 kg-grænsen" },
    { id: "udstedelsesdato", title: "Betydningen af kørekortets udstedelsesdato" },
    { id: "kategori-b-plus", title: "Kategori B+ (kode 96)" },
    { id: "kategori-be", title: "Kategori BE (stort trailerkort)" },
    { id: "sammenligning", title: "Sammenligningstabel for kategorier" }
  ];

  const faq = [
    {
      q: "Må jeg køre med en 1.500 kg trailer på et almindeligt B-kørekort?",
      a: "Ja, det må du godt, forudsat at to betingelser er opfyldt: 1) Bilens tilladte totalvægt plus trailerens tilladte totalvægt må tilsammen højst være 3.500 kg (bilen må altså højst have en tilladt totalvægt på 2.000 kg). 2) Bilen skal ifølge sin registreringsattest være godkendt til at trække mindst 1.500 kg."
    },
    {
      q: "Hvad ændrede sig for kørekort udstedt før 2009 eller 2013?",
      a: "Tidligere måtte man på B-kørekort højst trække en trailer, hvis tilladte totalvægt ikke oversteg bilens egenvægt plus 50 kg. Denne regel er bortfaldet. I dag ser man udelukkende på de tilladte totalvægte: trailer op til 750 kg er altid tilladt, og over 750 kg må bil og trailer tilsammen højst have 3.500 kg tilladt totalvægt."
    },
    {
      q: "Er det trailerens faktiske vægt eller tilladte totalvægt, der tæller for kørekortet?",
      a: "I forhold til dit kørekort gælder altid de tilladte totalvægte fra registreringsattesterne. Hvis traileren har en tilladt totalvægt på 2.000 kg, men er helt tom og kun vejer 350 kg, kræver den stadig BE-kørekort, hvis vogntogets tilladte totalvægte overstiger 3.500 kg."
    },
    {
      q: "Hvor meget må man køre med på et B+ kørekort?",
      a: "Med et B+ kørekort må vogntogets samlede tilladte totalvægt være op til 4.250 kg, uanset hvor tung traileren er (dog max hvad bilen må trække). Kategori B+ kræver en praktisk køreprøve, men ingen teoriprøve."
    }
  ];

  const sources = [
    { title: "Kørekortbekendtgørelsen (BEK nr. 1243 af 28/08/2020)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2020/1243" },
    { title: "Færdselsstyrelsen: Kørekort til påhængskøretøj til bil (B, B+, BE)", source: "fstyr.dk", url: "https://fstyr.dk/privat/koerekort/udvidelse-af-koerekort/koerekort-til-paahaengskoeretoej-til-bil" },
    { title: "Færdselsloven kapitel 9 om kørekort (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" }
  ];

  return (
    <>
      <SEO 
        title="Kørekort og trailer: Hvad må du trække med B, B+ og BE?" 
        description="Få præcise svar på hvad du må trække med kategori B, B+ og BE. Regler for tilladte totalvægte, 750 kg-grænsen og kørekortets udstedelsesdato." 
        pathname="/koerekort-og-trailer"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Kørekort og trailer: Hvad må du trække med B, B+ og BE?
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Med et almindeligt personbilskørekort (kategori B) må du altid køre med en trailer med en tilladt totalvægt på op til 750 kg. Hvis traileren er tungere end 750 kg, må bilens og trailerens tilladte totalvægte tilsammen højst udgøre 3.500 kg.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Overstiger den samlede tilladte totalvægt 3.500 kg, skal du have kategori B+ (op til 4.250 kg) eller det store trailerkørekort kategori BE (op til 7.000 kg). Det er altid tallene på køretøjernes registreringsattester – den <em>tilladte totalvægt</em> – der afgør, hvilket kørekort du skal have.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Kørekortsregel nr. 1">
          <p className="m-0">
            Kørekortets gyldighed vurderes ud fra <strong>papirvægte</strong> (tilladt totalvægt på registreringsattesterne). Bilens mekaniske trækevne vurderes ud fra <strong>den faktiske vægt</strong> på kontrolvægten. Læs mere i artiklen om <Link to="/hvor-meget-maa-man-traekke" className="text-[#2E3A46] font-semibold hover:text-[#B58200] underline">hvor meget man må trække</Link>.
          </p>
        </Callout>

        <h2 id="kategori-b" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Kategori B: Almindeligt kørekort
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Et almindeligt kategori B-kørekort giver dig ret til at føre en personbil eller varebil med en tilladt totalvægt på højst 3.500 kg, indrettet til befordring af højst 8 personer foruden føreren.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du tilkobler et påhængskøretøj på et B-kørekort, er der to forskellige situationer i lovgivningen:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Traileren har en tilladt totalvægt på højst 750 kg:</strong> Du må altid køre med traileren, uanset bilens tilladte totalvægt (så længe bilen må trække vægten). Det betyder, at en bil med 3.500 kg totalvægt må tilkobles en 750 kg trailer – samlet tilladt vogntogsvægt på 4.250 kg.</li>
          <li><strong>Traileren har en tilladt totalvægt på over 750 kg:</strong> Summen af bilens tilladte totalvægt og trailerens tilladte totalvægt må maksimalt være 3.500 kg.</li>
        </ul>

        <WeightDiagram caption="Figur 1: Vægtbegreber i vogntoget mellem bil og trailer." />

        <h2 id="750-kg-reglen" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          750 kg-reglen og 3.500 kg-grænsen
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Mange bilister tror fejlagtigt, at man aldrig må trække en trailer over 750 kg på et B-kørekort. Det er forkert. Du må gerne trække en trailer på eksempelvis 1.200 kg eller 1.500 kg på almindeligt kørekort, så længe bilens tilladte totalvægt er tilsvarende lav.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Her ses konkrete regneeksempler på, hvornår kategori B rækker, og hvornår der kræves trailerkørekort:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Bil totalvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Trailer totalvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Samlet totalvægt</th>
                <th className="py-2.5 font-semibold">Påkrævet kørekort</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4">1.800 kg</td>
                <td className="py-3 pr-4">750 kg</td>
                <td className="py-3 pr-4">2.550 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">Kategori B</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">3.500 kg</td>
                <td className="py-3 pr-4">750 kg</td>
                <td className="py-3 pr-4">4.250 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">Kategori B</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">2.000 kg</td>
                <td className="py-3 pr-4">1.500 kg</td>
                <td className="py-3 pr-4">3.500 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">Kategori B</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">2.200 kg</td>
                <td className="py-3 pr-4">1.500 kg</td>
                <td className="py-3 pr-4">3.700 kg</td>
                <td className="py-3 font-semibold text-[#B58200]">Kategori B+ eller BE</td>
              </tr>
              <tr>
                <td className="py-3 pr-4">2.600 kg</td>
                <td className="py-3 pr-4">2.000 kg</td>
                <td className="py-3 pr-4">4.600 kg</td>
                <td className="py-3 font-semibold text-[#B58200]">Kategori BE</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om betydningen af tallene på trailerens skilt i vores gennemgang af <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer totalvægt, egenvægt og lasteevne</Link>.
        </p>

        <h2 id="udstedelsesdato" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Betydningen af kørekortets udstedelsesdato (før 2009 og 2013)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Der hersker ofte forvirring om ældre kørekortregler:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Før 1. maj 2009:</strong> Dengang gjaldt en regel om, at trailerens tilladte totalvægt ikke måtte overstige bilens egenvægt plus 50 kg for at køre på B-kørekort. Denne regel blev afskaffet for at forenkle reglerne.</li>
          <li><strong>19. januar 2013 (EU's 3. kørekortdirektiv):</strong> Her blev kategorien B+ (kode 96) indført i Danmark. Samtidig blev der indført en begrænsning på BE-kørekortet på maksimalt 3.500 kg påhængskøretøj. Har du erhvervet dit BE-kørekort før den 19. januar 2013, må du fortsat køre med påhængsvogne over 3.500 kg, så længe de er forsynet med trykluftbremser eller el-bremser godkendt dertil.</li>
        </ul>

        <h2 id="kategori-b-plus" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Kategori B+ (kode 96)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Kategori B+ er et mellemkørekort, som tillader et vogntog med en samlet tilladt totalvægt på op til 4.250 kg, hvor traileren vejer over 750 kg.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Kategori B+ kræver 4 lektioners køreundervisning og en praktisk køreprøve hos Færdselsstyrelsen, men ingen teoriprøve. Da prisforskellen og undervisningskravene mellem B+ og BE er meget små hos køreskolerne, vælger langt de fleste at tage det fulde BE-kørekort med det samme.
        </p>

        <h2 id="kategori-be" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Kategori BE (stort trailerkort)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Med et kategori BE-kørekort må du føre en personbil eller varebil på op til 3.500 kg tilkoblet en trailer på op til 3.500 kg. Det giver en samlet maksimal vogntogsvægt på helt op til 7.000 kg.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Kategori BE er standarden for håndværkere, hesteejere, bådejere og bilister, der trækker store maskin- eller autotrailere. Læs hele forløbet i vores artikel om <Link to="/be-koerekort" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">BE-kørekort</Link>.
        </p>

        <h2 id="sammenligning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sammenligningstabel for kategorier
        </h2>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Kørekort</th>
                <th className="py-2.5 pr-4 font-semibold">Max bilvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Max trailervægt</th>
                <th className="py-2.5 font-semibold">Max vogntogsvægt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">B (lille trailer)</td>
                <td className="py-3 pr-4">3.500 kg</td>
                <td className="py-3 pr-4">750 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">4.250 kg</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">B (stor trailer)</td>
                <td className="py-3 pr-4">Op til 2.750 kg</td>
                <td className="py-3 pr-4">Over 750 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">3.500 kg</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">B+ (kode 96)</td>
                <td className="py-3 pr-4">3.500 kg</td>
                <td className="py-3 pr-4">Over 750 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">4.250 kg</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">BE</td>
                <td className="py-3 pr-4">3.500 kg</td>
                <td className="py-3 pr-4">3.500 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">7.000 kg</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Husk også at kontrollere <Link to="/trailer-hastighed" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">hastighedsgrænser for trailer</Link> samt de tekniske krav til <Link to="/trailer-bremser" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer med og uden bremser</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
